import { CITY_COORDINATES } from '../data/geocoding';

export const cleanTitleForProject = (title) => {
    if (!title) return "";
    let cleaned = title.replace(/[–—]/g, "-");
    cleaned = cleaned.replace(/\s*-\s*(?:Bâtiment\s+\w+|Lot\s+(?:n°)?\w+|n°\s*\w+|Appartement\s+[TF]\d+|[TF]\d+).*/gi, "");
    cleaned = cleaned.replace(/\s*(?:Bâtiment\s+\w+|Lot\s+(?:n°)?\w+|n°\s*\w+|Appartement\s+[TF]\d+|[TF]\d+).*/gi, "");
    cleaned = cleaned.replace(/\s*(?:lot|bâtiment|batiment|appt|apprt|appart)\s*$/gi, "");
    return cleaned.trim();
};

export const hasLotIndicator = (title) => {
    if (!title) return false;
    return /\b(lot|batiment|bâtiment|bat|app|bloc|escalier|n°|t\d+|f\d+)\b/i.test(title);
};

export const normalize = (str) => {
    if (!str) return "";
    return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

/**
 * Groups a list of properties into projects and single listings
 * @param {Array} properties 
 * @returns {Array} List of grouped or single items
 */
export function groupProperties(properties) {
    if (!properties || properties.length === 0) return [];

    const projectGroups = new Map();

    for (const prop of properties) {
        const isLot = hasLotIndicator(prop.title);
        const cleanTitle = cleanTitleForProject(prop.title);
        const normTitle = normalize(cleanTitle);
        const cityNorm = normalize(prop.city);
        
        const lat = prop.latitude;
        const lon = prop.longitude;
        const cityKey = (prop.city || "").toLowerCase().trim();
        const cityCenter = CITY_COORDINATES[cityKey] || CITY_COORDINATES[cityKey.replace(/\s+/g, '-')] || null;
        const isFallbackGPS = cityCenter && Math.abs(cityCenter[0] - lat) < 0.00001 && Math.abs(cityCenter[1] - lon) < 0.00001;
        const hasGPS = lat && lon && lat !== 0 && lon !== 0 && !isFallbackGPS;

        let groupKey = null;
        const propStatus = prop.status || 'Vente';

        // Try to find matching group key
        for (const [key, val] of projectGroups.entries()) {
            // Regrouper les ventes avec les ventes et les locations avec les locations
            if (val.status !== propStatus) {
                continue;
            }

            // Criterion 1: GPS Match
            if (hasGPS && val.gps && Math.abs(val.gps.lat - lat) < 0.00005 && Math.abs(val.gps.lon - lon) < 0.00005) {
                if (isLot || val.hasLotIndicator) {
                    groupKey = key;
                    break;
                }
            }
            // Criterion 2: Title and City match, with lot indicator
            if (normTitle && val.normalizedTitle === normTitle && val.cityNorm === cityNorm) {
                if (isLot || val.hasLotIndicator) {
                    groupKey = key;
                    break;
                }
            }
        }

        if (groupKey) {
            const grp = projectGroups.get(groupKey);
            grp.properties.push(prop);
            if (isLot) grp.hasLotIndicator = true;
        } else {
            const newKey = prop.id || prop.netty_ref;
            projectGroups.set(newKey, {
                key: newKey,
                title: cleanTitle || prop.title,
                normalizedTitle: normTitle,
                cityNorm,
                gps: hasGPS ? { lat, lon } : null,
                hasLotIndicator: isLot,
                status: propStatus,
                properties: [prop]
            });
        }
    }

    const list = [];

    for (const grp of projectGroups.values()) {
        if (grp.properties.length > 1) {
            // Sort properties naturally
            const sortedProps = [...grp.properties].sort((a, b) => 
                (a.title || "").localeCompare(b.title || "", undefined, { numeric: true, sensitivity: 'base' })
            );

            const prices = sortedProps.map(p => p.price || 0).filter(Boolean);
            const surfaces = sortedProps.map(p => p.surface || p.surface_terrain || 0).filter(Boolean);
            const rooms = sortedProps.map(p => p.rooms || p.nb_pieces || 0).filter(Boolean);

            const minPrice = prices.length ? Math.min(...prices) : 0;
            const maxPrice = prices.length ? Math.max(...prices) : 0;

            const minSurface = surfaces.length ? Math.min(...surfaces) : 0;
            const maxSurface = surfaces.length ? Math.max(...surfaces) : 0;

            const minRooms = rooms.length ? Math.min(...rooms) : 0;
            const maxRooms = rooms.length ? Math.max(...rooms) : 0;

            // Use first lot as representative for other properties
            const rep = sortedProps[0];

            list.push({
                ...rep,
                isProject: true,
                title: grp.title,
                price: minPrice, // for compatibility
                priceMin: minPrice,
                priceMax: maxPrice,
                surface: minSurface, // for compatibility
                surfaceMin: minSurface,
                surfaceMax: maxSurface,
                rooms: minRooms, // for compatibility
                roomsMin: minRooms,
                roomsMax: maxRooms,
                lotsCount: sortedProps.length,
                lots: sortedProps,
                is_favorite: sortedProps.some(p => p.is_favorite)
            });
        } else {
            list.push(grp.properties[0]);
        }
    }

    return list;
}
